// packages/web-ui/src/panels/views/layout-postpass.ts
//
// LV1-S4 — Tier-2 deterministic positions post-pass.
//
// Closed-form geometry transforms layered ON TOP of an ELK-computed layout: pin
// individual nodes to one of nine bbox anchors, align node groups onto a shared
// mean axis (row = mean-y, column = mean-x), and honour emphasis-enlarged node
// sizes. The output is a sparse `positions` override map (`Record<id,{x,y}>`)
// consumed verbatim by `ViewRenderer`'s existing `layout.positions` merge.
//
// Two load-bearing invariants the unit tests pin:
//   * Switch-OFF parity — no pins/groups/emphasis ⇒ an EMPTY map (the no-intent
//     render is byte-identical to today; nothing is moved).
//   * Idempotency — re-feeding the function its own output is a no-op.
//     `ViewRenderer` re-runs ELK up to 3× and re-applies this pass each time, so
//     a non-fixed-point transform would oscillate. Each step is a fixed point:
//       - Pins are absolute functions of the STABLE frame bbox (the directive-
//         untouched nodes), so a re-fed pin re-derives the same coordinate.
//       - Group alignment collapses members onto a shared axis; overlapping
//         members are then separated ALONG that axis by a deterministic gap-pack
//         keyed off the frozen cross-axis order + sizes (never a prior packed
//         coord), so a re-fed group re-derives the same coordinates.
//       - The collision-nudge fires only on a strict overlap and converges to a
//         globally overlap-free moved set; a grouped member is axis-locked so the
//         nudge never disturbs its shared alignment coordinate, keeping the
//         alignment mean a fixed point across re-feeds.
//   * No overlaps — after the post-pass, no two nodes in the moved set strictly
//     overlap any node (the nudge sweeps the whole moved set until a complete
//     scan is clean, fixing the prior single-pass 3+-pile-up gap).
//
// Pure module: NO react / DOM / ELK / `node:` imports. The DSL types are
// imported (never redefined) from the LV1-S1 SSOT.

import type { Node } from '@xyflow/react';
import type {
  LayoutIntent,
  LayoutAnchor,
  LayoutGroup,
} from '@tessa-lean/base/api-schemas/index.js';

import { LEAF_W } from './layout-builder.js';

/**
 * Multiplier applied to an emphasised node's width/height BEFORE it is handed to
 * ELK, so the graph reflows around the larger box (AC #1). Exported so both the
 * `enlargeEmphasis` helper and its unit test reference one constant. A fixed
 * scale applied to the ORIGINAL dimension (never compounded) keeps the enlarge
 * step idempotent across `ViewRenderer`'s up-to-3× ELK re-runs.
 */
export const EMPHASIS_SCALE = 1.5;

/**
 * Framework-light positioned-node view used by the pure geometry. Derived from a
 * React Flow `Node` by reading `position.x/y` + `width`/`height`. Keeping the
 * post-pass over this minimal shape (rather than `Node`) makes it trivially
 * testable with plain object literals and free of `@xyflow/react` runtime deps.
 */
export interface PositionedNode {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Axis-aligned bounding box of a node set, plus its mid-point. */
interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  midX: number;
  midY: number;
}

/** A plain `{x,y}` position, the shape of a `layout.positions` entry. */
export interface Position {
  x: number;
  y: number;
}

/**
 * Read a React Flow `Node`'s placed geometry into the framework-light
 * `PositionedNode`. Width/height fall back to the ELK leaf default / a square
 * when a node carries no measured size (degenerate, but keeps the math total).
 */
export function toPositionedNode(node: Node): PositionedNode {
  const w =
    typeof node.width === 'number' && node.width > 0 ? node.width : LEAF_W;
  const h =
    typeof node.height === 'number' && node.height > 0 ? node.height : LEAF_W;
  return { id: node.id, x: node.position.x, y: node.position.y, w, h };
}

/**
 * Closed-form AABB of a node set. Returns `null` for an empty set (caller
 * no-ops). `width`/`height` are read off each node so the box bounds the placed
 * extents, not just the top-left points.
 */
function computeBBox(nodes: ReadonlyArray<PositionedNode>): BBox | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    if (n.x < minX) minX = n.x;
    if (n.y < minY) minY = n.y;
    if (n.x + n.w > maxX) maxX = n.x + n.w;
    if (n.y + n.h > maxY) maxY = n.y + n.h;
  }
  return {
    minX,
    minY,
    maxX,
    maxY,
    midX: (minX + maxX) / 2,
    midY: (minY + maxY) / 2,
  };
}

/**
 * Top-left position that lands a node of size `(w,h)` at `anchor` relative to
 * `bbox`. Corners pin to a bbox extremum; far-edges pin one axis to the bbox
 * edge and centre the other; `center` is bbox-mid − half-size. A pure lookup
 * over the nine `LayoutAnchor` literals — re-applying it yields the same coord
 * (the absolute-function half of the idempotency guarantee).
 */
function anchorPosition(
  anchor: LayoutAnchor,
  w: number,
  h: number,
  bbox: BBox,
): Position {
  const { minX, minY, maxX, maxY, midX, midY } = bbox;
  const cx = midX - w / 2; // horizontally-centred x
  const cy = midY - h / 2; // vertically-centred y
  const rx = maxX - w; // right-flush x
  const by = maxY - h; // bottom-flush y
  switch (anchor) {
    case 'top-left':
      return { x: minX, y: minY };
    case 'top':
      return { x: cx, y: minY };
    case 'top-right':
      return { x: rx, y: minY };
    case 'left':
      return { x: minX, y: cy };
    case 'center':
      return { x: cx, y: cy };
    case 'right':
      return { x: rx, y: cy };
    case 'bottom-left':
      return { x: minX, y: by };
    case 'bottom':
      return { x: cx, y: by };
    case 'bottom-right':
      return { x: rx, y: by };
    default: {
      // Exhaustiveness guard — a new anchor literal fails typecheck here.
      const _never: never = anchor;
      return _never;
    }
  }
}

/** Do two AABBs strictly overlap (touching edges do NOT count as overlap)? */
function overlaps(a: PositionedNode, b: PositionedNode): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

/**
 * Enlarge emphasised nodes' `width`/`height` (and mirrored `style.width/height`)
 * by {@link EMPHASIS_SCALE} so ELK reflows around the larger boxes (AC #1, the
 * one transform that must run PRE-ELK). Pure: returns a fresh array; nodes not
 * in `intent.emphasis` are passed through by reference. Empty emphasis ⇒ the
 * input array is returned unchanged (switch-OFF parity).
 *
 * Applied to the ORIGINAL source dimensions on every fresh ELK input, never to
 * an already-enlarged value, so the up-to-3× ELK re-runs do not compound.
 */
export function enlargeEmphasis(
  nodes: ReadonlyArray<Node>,
  intent: LayoutIntent | undefined,
): Node[] {
  const ids = intent?.emphasis ?? [];
  if (ids.length === 0) return nodes as Node[];
  const emphasised = new Set(ids);
  return nodes.map((n) => {
    if (!emphasised.has(n.id)) return n;
    const baseW =
      typeof n.width === 'number' && n.width > 0 ? n.width : LEAF_W;
    const baseH =
      typeof n.height === 'number' && n.height > 0 ? n.height : LEAF_W;
    const w = Math.round(baseW * EMPHASIS_SCALE);
    const h = Math.round(baseH * EMPHASIS_SCALE);
    return {
      ...n,
      width: w,
      height: h,
      style: { ...(n.style ?? {}), width: w, height: h },
    };
  });
}

/** Does an intent request any Tier-2 transform (pins / groups / emphasis)? */
function hasTier2Directives(intent: LayoutIntent): boolean {
  return (
    intent.pins.length > 0 ||
    intent.groups.length > 0 ||
    intent.emphasis.length > 0
  );
}

/**
 * Minimum gap inserted between two group members when the along-axis
 * de-collision packing has to spread an overlapping row/column. A fixed pixel
 * constant (never derived from prior positions) so the packing is a pure
 * function of member order + sizes — the fixed-point property that keeps
 * group + nudge idempotent across `ViewRenderer`'s up-to-3× re-runs.
 */
const GROUP_PACK_GAP = 24;

/**
 * Resolve a group's member nodes (skipping ids absent from the working set) and
 * apply the shared-axis mean: `row` sets every member's `y` to a shared value,
 * `column` sets every member's `x`. A group with fewer than two resolvable
 * members is ignored (no axis to share) and returns no moved ids.
 *
 * Idempotency under pin composition: the shared value is the mean of the
 * **pinned** members' axis when any member is pinned, otherwise the mean of all
 * members. A pin re-derives to the same coord on every pass, so aligning the
 * rest to a pinned member's axis is a fixed point; with no pin, the mean of
 * values already collapsed onto that mean is the same mean.
 *
 * De-collision (LV1-S4 review fix): after aligning, members can pile up on the
 * shared axis (e.g. a `row` group whose members all share an x). Aligned members
 * are separated ALONG the alignment axis (a `row` spreads x, a `column` spreads
 * y) by a deterministic gap-pack: sort the members by their CROSS-axis position
 * (the axis the align step did NOT touch, tie-broken by id), keep the first in
 * place, and lay each subsequent member exactly `GROUP_PACK_GAP` past the
 * running extent of the ones before it. Two properties make this a fixed point —
 * (1) the pack only fires when members actually overlap, so a separated row is
 * untouched; (2) the sort key (cross-axis coord) is itself frozen by the align
 * step (the cross axis is never moved here), and the packed coords are a pure
 * function of {sorted order, sizes, gap, the first member's frozen along-coord},
 * never of a previously-packed coord. So re-feeding the packed output re-derives
 * the same packed output: a no-op. Cross-axis-vs-frame overlaps are left to the
 * globally-convergent {@link nudgeCollisions} sweep.
 *
 * Mutates `working` in place; returns the ids it moved.
 */
function applyGroup(
  group: LayoutGroup,
  working: Map<string, PositionedNode>,
  pinnedIds: ReadonlySet<string>,
): string[] {
  const members: PositionedNode[] = [];
  for (const id of group.nodes) {
    const node = working.get(id);
    if (node !== undefined) members.push(node);
  }
  if (members.length < 2) return [];
  // Anchor the alignment to the pinned members if any (stable), else all members.
  const pinnedMembers = members.filter((n) => pinnedIds.has(n.id));
  const basis = pinnedMembers.length > 0 ? pinnedMembers : members;
  const isRow = group.align === 'row';

  // 1) Shared-axis mean — row pins y, column pins x.
  const shared =
    basis.reduce((sum, n) => sum + (isRow ? n.y : n.x), 0) / basis.length;
  const aligned: PositionedNode[] = members.map((n) =>
    isRow ? { ...n, y: shared } : { ...n, x: shared },
  );

  // 2) Along-axis de-collision pack — only if any two ALIGNED members overlap.
  // The along axis is x for a row, y for a column.
  const packed = packAlongAxis(aligned, isRow);

  const moved: string[] = [];
  for (const n of packed) {
    working.set(n.id, n);
    moved.push(n.id);
  }
  return moved;
}

/**
 * Deterministic, idempotent along-axis packing of an already-aligned member set.
 * `isRow` ⇒ pack along x (members share y); else pack along y. Returns the input
 * UNCHANGED (by value) when no two members overlap — a separated group is a
 * no-op, preserving switch-OFF-adjacent behaviour and idempotency. Otherwise
 * members are laid edge-to-edge with a {@link GROUP_PACK_GAP} gap, ordered by
 * their CROSS-axis coordinate (frozen by the align step) then id, starting from
 * the minimum along-coordinate among the members so the packed run stays anchored
 * to where the group already was (no net drift on re-feed).
 */
function packAlongAxis(
  members: ReadonlyArray<PositionedNode>,
  isRow: boolean,
): PositionedNode[] {
  // Detect any strict overlap among members; if none, nothing to pack.
  let anyOverlap = false;
  for (let i = 0; i < members.length && !anyOverlap; i += 1) {
    for (let j = i + 1; j < members.length; j += 1) {
      if (overlaps(members[i]!, members[j]!)) {
        anyOverlap = true;
        break;
      }
    }
  }
  if (!anyOverlap) return [...members];

  // Stable order: by the CROSS-axis coord (untouched by the align step, so it is
  // a fixed sort key across re-feeds), tie-broken by id.
  const crossOf = (n: PositionedNode): number => (isRow ? n.x : n.y);
  const sorted = [...members].sort((a, b) => {
    const ca = crossOf(a);
    const cb = crossOf(b);
    if (ca !== cb) return ca - cb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  // Anchor the run at the minimum along-coordinate among the members so the
  // packed block keeps its position (a fixed point: the min of the packed
  // output equals the start cursor, which re-derives identically).
  const alongStart = sorted.reduce(
    (min, n) => Math.min(min, isRow ? n.x : n.y),
    Infinity,
  );

  const result: PositionedNode[] = [];
  let cursor = alongStart;
  for (const n of sorted) {
    const placed = isRow ? { ...n, x: cursor } : { ...n, y: cursor };
    result.push(placed);
    cursor += (isRow ? n.w : n.h) + GROUP_PACK_GAP;
  }
  return result;
}

/** Hard ceiling on the global de-collision rounds (defence against a
 * pathological configuration that can never fully separate, e.g. more movers
 * than the frame can hold). Generous — real views clear in 1-3 rounds. */
const NUDGE_MAX_ROUNDS = 64;

/** A push direction the nudge can move a node along. */
type NudgeDirection = '+x' | '-x' | '+y' | '-y';

/**
 * Deterministic, globally-convergent collision-nudge (LV1-S4 review fix,
 * re-worked). For every node the post-pass moved, push it clear of the nodes it
 * strictly overlaps — and keep sweeping the WHOLE moved set until a complete
 * scan finds zero overlaps anywhere (or {@link NUDGE_MAX_ROUNDS} rounds elapse).
 *
 * Two properties fix the failure modes of the earlier per-pair push:
 *
 *  • **Clear-all displacement** — a mover is displaced, in ONE move, far enough
 *    to clear EVERY node it currently overlaps (the union extent along the
 *    chosen direction), instead of the pairwise overlap extent. A pairwise push
 *    out of one obstacle frequently landed exactly inside the next one.
 *  • **Per-mover momentum** — once a mover has picked a push direction it keeps
 *    that direction on every later move. Without this, a mover SANDWICHED
 *    between two obstacles (one above, one below) ping-pongs between them until
 *    the round budget dies and the overlap survives (observed with a
 *    bottom-right pin landing between two stacked frame nodes). Movement along
 *    a fixed ray is monotone, so the mover always exits the finite obstacle
 *    field.
 *
 * Determinism: movers are swept in stable id order; candidate directions are
 * evaluated in the fixed order `+x, -x, +y, -y`; the shortest clear-all
 * displacement wins (ties → earlier candidate). No randomness, time, or float
 * drift beyond the inputs.
 *
 * Idempotency: the nudge only fires on STRICT overlap. On `ViewRenderer`'s
 * re-feed the merged source already carries the cleared coords, so the
 * convergence scan finds no overlap and leaves every node where it is — the
 * cleared scene is a fixed point.
 *
 * Axis-lock (the group-idempotency half of the LV1-S4 review fix): a grouped
 * member's SHARED axis must never be disturbed, else the re-feed re-derives a
 * drifted alignment mean. So `axisLock` maps such a member id to the axis it
 * MAY move on (`'x'` for a row member, `'y'` for a column member); the nudge
 * only offers that member the two directions of its free axis. Un-grouped
 * movers (pins) have no lock and choose among all four directions.
 *
 * Complexity: O(rounds · movers · nodes), bounded by {@link NUDGE_MAX_ROUNDS}.
 * Fine at current view sizes (tens of nodes); revisit if views grow to hundreds
 * of movable nodes.
 */
function nudgeCollisions(
  working: Map<string, PositionedNode>,
  movedIds: ReadonlySet<string>,
  axisLock: ReadonlyMap<string, 'x' | 'y'>,
): void {
  // Stable, id-sorted node list so every sweep order is deterministic.
  const orderedIds = [...working.values()]
    .map((n) => n.id)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  // Only nudge nodes we actually moved; un-moved (ELK-placed) nodes are the
  // authoritative obstacles and never shift.
  const moverIds = orderedIds.filter((id) => movedIds.has(id));
  if (moverIds.length === 0) return;

  // Momentum: the direction each mover committed to on its first displacement.
  const momentum = new Map<string, NudgeDirection>();

  for (let round = 0; round < NUDGE_MAX_ROUNDS; round += 1) {
    let anyMovedThisRound = false;
    for (const moverId of moverIds) {
      const current = working.get(moverId)!;
      // ALL nodes this mover strictly overlaps right now.
      const obstacles: PositionedNode[] = [];
      for (const otherId of orderedIds) {
        if (otherId === moverId) continue;
        const other = working.get(otherId)!;
        if (overlaps(current, other)) obstacles.push(other);
      }
      if (obstacles.length === 0) continue;

      // Clear-all displacement per candidate direction: how far the mover must
      // travel along that direction so that NONE of the current obstacles
      // still strictly overlaps it (their union extent along the ray).
      const lock = axisLock.get(moverId);
      const candidates: Array<{ dir: NudgeDirection; dist: number }> = [];
      if (lock !== 'y') {
        const maxRight = Math.max(...obstacles.map((o) => o.x + o.w));
        const minLeft = Math.min(...obstacles.map((o) => o.x));
        candidates.push(
          { dir: '+x', dist: maxRight - current.x },
          { dir: '-x', dist: current.x + current.w - minLeft },
        );
      }
      if (lock !== 'x') {
        const maxBottom = Math.max(...obstacles.map((o) => o.y + o.h));
        const minTop = Math.min(...obstacles.map((o) => o.y));
        candidates.push(
          { dir: '+y', dist: maxBottom - current.y },
          { dir: '-y', dist: current.y + current.h - minTop },
        );
      }

      // Keep a previously-committed direction (monotone escape); otherwise
      // pick the shortest clear-all displacement (ties → candidate order).
      const committed = momentum.get(moverId);
      let chosen = committed !== undefined
        ? candidates.find((c) => c.dir === committed)
        : undefined;
      chosen ??= candidates.reduce((best, c) => (c.dist < best.dist ? c : best));

      const moved: PositionedNode =
        chosen.dir === '+x'
          ? { ...current, x: current.x + chosen.dist }
          : chosen.dir === '-x'
            ? { ...current, x: current.x - chosen.dist }
            : chosen.dir === '+y'
              ? { ...current, y: current.y + chosen.dist }
              : { ...current, y: current.y - chosen.dist };
      working.set(moverId, moved);
      momentum.set(moverId, chosen.dir);
      anyMovedThisRound = true;
    }
    // A full round with no displacement ⇒ globally overlap-free among movers.
    if (!anyMovedThisRound) return;
  }
}

/**
 * Global residual-overlap resolution (Tier-2.5 safety net). Returns a sparse
 * `positions` override map separating EVERY strictly-overlapping node in the
 * input set — or an EMPTY map when the input is already overlap-free (the
 * overwhelmingly common case, so the no-intent / switch-OFF render path stays
 * byte-identical).
 *
 * Exists because Tier-1 (ELK) output is not universally trustworthy: elkjs's
 * radial algorithm returned EVERY node of a cyclic graph at the same coordinate
 * (the "all blocks overlapping in the middle" report), and a directive
 * composition can leave residual overlaps the per-directive nudge did not own.
 * The sweep treats every node as movable and reuses {@link nudgeCollisions}
 * (clear-all displacement + momentum), so it is deterministic and idempotent —
 * feeding its own output back yields no further movement.
 *
 * Caller contract: pass ROOT-LEVEL nodes only. Children of a container live in
 * parent-relative coordinates, and a container legitimately "overlaps" its own
 * children — sweeping those would wreck nested IBDs.
 */
export function resolveResidualOverlaps(
  positionedNodes: ReadonlyArray<PositionedNode>,
): Record<string, Position> {
  let anyOverlap = false;
  for (let i = 0; i < positionedNodes.length && !anyOverlap; i += 1) {
    for (let j = i + 1; j < positionedNodes.length; j += 1) {
      if (overlaps(positionedNodes[i]!, positionedNodes[j]!)) {
        anyOverlap = true;
        break;
      }
    }
  }
  if (!anyOverlap) return {};

  const working = new Map<string, PositionedNode>();
  const allIds = new Set<string>();
  for (const n of positionedNodes) {
    working.set(n.id, { ...n });
    allIds.add(n.id);
  }
  nudgeCollisions(working, allIds, new Map());

  const out: Record<string, Position> = {};
  for (const n of positionedNodes) {
    const after = working.get(n.id)!;
    if (after.x !== n.x || after.y !== n.y) out[n.id] = { x: after.x, y: after.y };
  }
  return out;
}

/**
 * Apply the Tier-2 deterministic post-pass to an ELK-placed node set.
 *
 * Returns a SPARSE `positions` override map containing ONLY the nodes the
 * transform moved (pins, group members, and any node a collision-nudge
 * displaced). When the intent requests no pins/groups/emphasis the map is empty
 * — switch-OFF parity, the no-intent render is byte-identical to today.
 *
 * Order: pins first (absolute bbox anchors), then groups (read the post-pin
 * working set so an explicit pin + align composes, with overlapping members
 * separated along the alignment axis by a deterministic gap-pack), then a
 * globally-convergent collision-nudge that leaves the moved set overlap-free.
 * Idempotent — re-applying to its own output yields the same map: anchors are
 * absolute over the stable frame, the group gap-pack re-derives from the frozen
 * cross-axis order, grouped members are axis-locked so the nudge cannot drift
 * the alignment mean, and the nudge only fires on strict overlap.
 *
 * Pure + total: cannot fail, so it returns the map directly rather than a
 * `Result` (mirrors the LV1-S1 pure-constructor precedent — wrapping a total
 * transform in a never-`err` `Result` adds a dead branch with no caller value).
 */
export function applyLayoutPostpass(
  positionedNodes: ReadonlyArray<PositionedNode>,
  intent: LayoutIntent | undefined,
): Record<string, Position> {
  if (intent === undefined || !hasTier2Directives(intent)) return {};

  // Working set keyed by id; mutated by pins → groups → nudge.
  const working = new Map<string, PositionedNode>();
  for (const n of positionedNodes) working.set(n.id, { ...n });
  const movedIds = new Set<string>();

  // ── Anchor frame ── the bbox pins are placed relative to MUST be stable
  // across re-application, else a second pass (whose input already has the
  // pinned nodes moved) would re-derive different anchor coords and break
  // idempotency. So we anchor against the bbox of the nodes the directives do
  // NOT touch (pinned ∪ grouped excluded) — that set is identical on the first
  // and any subsequent pass. When every node is touched (no stable frame), fall
  // back to the full-set bbox (a single-pin/all-grouped view is its own frame
  // and still re-derives to itself because the moved coords feed back unchanged).
  const directiveTouched = new Set<string>();
  for (const pin of intent.pins) directiveTouched.add(pin.node);
  for (const group of intent.groups) {
    for (const id of group.nodes) directiveTouched.add(id);
  }
  const frameNodes = positionedNodes.filter((n) => !directiveTouched.has(n.id));
  const bbox =
    computeBBox(frameNodes.length > 0 ? frameNodes : positionedNodes);
  if (bbox === null) return {};

  // ── Pins ── absolute anchors over the stable frame. Unknown ids ignored.
  const pinnedIds = new Set<string>();
  for (const pin of intent.pins) {
    const node = working.get(pin.node);
    if (node === undefined) continue;
    const pos = anchorPosition(pin.anchor, node.w, node.h, bbox);
    working.set(node.id, { ...node, x: pos.x, y: pos.y });
    movedIds.add(node.id);
    pinnedIds.add(node.id);
  }

  // ── Groups ── shared-axis mean over the post-pin working set, anchored to any
  // pinned members so the composition is idempotent. Only members of an applied
  // (≥2-member) group are marked moved. Each aligned member is locked to its
  // FREE axis (row ⇒ free x, column ⇒ free y) so the downstream nudge can never
  // disturb its shared alignment coordinate (group + nudge idempotency).
  const axisLock = new Map<string, 'x' | 'y'>();
  for (const group of intent.groups) {
    const moved = applyGroup(group, working, pinnedIds);
    const freeAxis: 'x' | 'y' = group.align === 'row' ? 'x' : 'y';
    for (const id of moved) {
      movedIds.add(id);
      // Last group wins on conflict — deterministic by group order.
      axisLock.set(id, freeAxis);
    }
  }

  // ── Collision-nudge ── separate moved nodes from any overlap, holding each
  // grouped member to its free axis (axisLock).
  nudgeCollisions(working, movedIds, axisLock);

  // Emit ONLY moved nodes (sparse override). A node whose final coord equals its
  // ELK input is still emitted if it was a pin/group target — that keeps the map
  // an explicit record of the directives, and idempotency holds because the
  // emitted coord re-derives to itself on a second pass.
  const out: Record<string, Position> = {};
  for (const id of movedIds) {
    const n = working.get(id);
    if (n !== undefined) out[id] = { x: n.x, y: n.y };
  }
  return out;
}
